import api from './api'

export interface BalanceItem {
  beneficiary_id: string
  beneficiary_name: string
  total_obligations: number
  total_paid: number
  outstanding: number
}

export interface BalanceSummary {
  items: BalanceItem[]
  total_outstanding: number
}

export interface PortalReceipt {
  id: string
  receipt_number: string
  amount: number
  payment_method: string
  payment_date: string
  description: string
  pdf_url: string | null
  created_at: string
}

export interface ReceiptListResponse {
  items: PortalReceipt[]
  total: number
  page: number
  pages: number
}

export interface PortalMilestone {
  id: string
  installment_number: number
  amount: number
  paid_amount: number
  due_date: string
  status: string
}

export interface PaymentPlan {
  id: string
  total_amount: number
  amount_paid: number
  frequency: string
  status: string
  start_date: string
  end_date?: string | null
  number_of_installments?: number
  milestones?: PortalMilestone[]
  created_at: string
}

export interface CreatePortalPaymentPlanPayload {
  beneficiary_id?: string
  obligation_id?: string
  milestones: Array<{ due_date: string; amount: number }>
  notes?: string
}

export interface TokenVerification {
  tenant_id: string
  client_id: string
  action: string
}

export async function verifyToken(): Promise<TokenVerification> {
  const res = await api.get('/verify-token')
  return res.data
}

// --- Phone OTP Login ---

export interface PhoneLoginResponse {
  token: string
  expires_in: number
  client_id: string
  client_name: string
  tenant_name: string
}

export interface PhoneLoginTenantOption {
  tenant_id: string
  tenant_name: string
  client_id: string
  client_name: string
}

export interface PhoneLoginMultiTenantResponse {
  requires_selection: boolean
  tenants: PhoneLoginTenantOption[]
}

export async function phoneLogin(
  firebaseToken: string,
): Promise<PhoneLoginResponse | PhoneLoginMultiTenantResponse> {
  const res = await api.post('/phone-login', { firebase_token: firebaseToken })
  return res.data
}

export async function phoneLoginSelectTenant(
  firebaseToken: string,
  tenantId: string,
): Promise<PhoneLoginResponse> {
  const res = await api.post('/phone-login/select-tenant', {
    firebase_token: firebaseToken,
    tenant_id: tenantId,
  })
  return res.data
}

export async function getBalance(): Promise<BalanceSummary> {
  const res = await api.get('/balance')
  return res.data
}

export async function getReceipts(page = 1, perPage = 20): Promise<ReceiptListResponse> {
  const res = await api.get('/receipts', { params: { page, per_page: perPage } })
  return res.data
}

export async function getReceiptPdfUrl(receiptId: string): Promise<string> {
  const res = await api.get(`/receipts/${receiptId}/pdf`)
  return res.data.pdf_url
}

export async function getPaymentPlans(): Promise<PaymentPlan[]> {
  const res = await api.get('/payment-plans')
  return res.data
}

// --- Magic Pay Link ---

export interface MagicPayVerification {
  tenant_id: string
  client_id: string
  amount: number
  beneficiary_id: string
  portal_token: string
}

export async function verifyMagicPayToken(token: string): Promise<MagicPayVerification> {
  const res = await api.post('/magic-pay/verify', { token })
  return res.data
}

// --- Pay Now (STK Push) ---

export interface PayInitiateResponse {
  request_id: string
  confirmation_message: string
  amount: number
  beneficiary_name: string
}

export interface PayStatusResponse {
  status: 'pending' | 'pending_confirmation' | 'success' | 'failed' | 'reversed' | 'unknown'
  message: string
  transaction_reference: string | null
  amount: number | null
}

export async function initiatePayment(
  beneficiaryId: string,
  amount: number,
  phoneOverride?: string,
  acrossAccounts = false,
): Promise<PayInitiateResponse> {
  const res = await api.post('/pay/initiate', {
    beneficiary_id: beneficiaryId,
    amount,
    phone_override: phoneOverride || null,
    ...(acrossAccounts ? { across_accounts: true } : {}),
  })
  return res.data
}

export async function confirmPayment(requestId: string): Promise<{ status: string; message: string }> {
  const res = await api.post('/pay/confirm', { request_id: requestId })
  return res.data
}

export async function getPaymentStatus(requestId: string): Promise<PayStatusResponse> {
  const res = await api.get(`/pay/status/${requestId}`)
  return res.data
}

// --- Payment Plan Modification ---

export interface PlanModificationRequest {
  reason: string
  requested_changes: Record<string, unknown>
}

export interface PlanModificationResponse {
  id: string
  plan_id: string
  status: string
  requested_changes: Record<string, unknown>
  created_at: string
}

export async function requestPlanModification(
  planId: string,
  payload: PlanModificationRequest,
): Promise<PlanModificationResponse> {
  const res = await api.post(`/payment-plans/${planId}/request-modification`, payload)
  return res.data
}
